import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router';
import { Combobox } from '@base-ui/react/combobox';
import { Check, ChevronsUpDown, Loader2, Plus, Search } from 'lucide-react';
import { toast } from 'sonner';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import {
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
} from '@/components/ui/sidebar';
import { useWorkspace } from '@/contexts/workspace-context';
import { useFetch } from '@/hooks/use-fetch';
import { workspaceUrl } from '@/lib/workspace-url';
import { emit } from '@/lib/analytics';
import type { Workspace } from '@/types/workspace';

// Empty-query browse cap: show the first N workspaces from the already-loaded
// context so the menu opens instantly without mounting 600–1000 DOM rows. Once
// the user types, the server-side search endpoint takes over (it matches the
// channel phone / WABA name / IG handle the client never loads) and returns
// only the matches.
const MAX_VISIBLE = 50;

/** A row in the switcher list — either a browse entry or a search hit. */
interface SwitcherItem {
  publicId: string;
  name: string;
  /** Channel label of the matching channel (search hits only). */
  subtitle: string | null;
  role: 'admin' | 'member';
}

/** Result row of GET /organizations/:orgId/workspaces/search. */
interface WorkspaceSearchResult {
  publicId: string;
  name: string;
  matchedChannel: { type: string; label: string } | null;
}

export function WorkspaceSwitcher() {
  const { activeWorkspace, workspaces, switchWorkspace, refreshWorkspaces } =
    useWorkspace();
  const fetchWithAuth = useFetch();
  const navigate = useNavigate();
  const [creating, setCreating] = useState(false);
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [searchResults, setSearchResults] = useState<WorkspaceSearchResult[] | null>(
    null,
  );
  const [searching, setSearching] = useState(false);

  const orgPublicId = activeWorkspace?.organizationPublicId;

  // Server-side search (debounced). The client only holds workspace names; the
  // endpoint matches channel phone / WABA name / IG handle too — the things
  // people actually remember — and returns only the matches, so we never ship
  // channel data app-wide. Empty query → no fetch; the browse list (the
  // already-loaded context) renders instead. All setState happens inside the
  // timeout callback so nothing fires synchronously in the effect body
  // (react-hooks/set-state-in-effect).
  useEffect(() => {
    const q = query.trim();
    const handle = setTimeout(
      () => {
        void (async () => {
          if (!q || !orgPublicId) {
            setSearchResults(null);
            setSearching(false);
            return;
          }
          setSearching(true);
          try {
            const res = await fetchWithAuth(
              // Switcher offers TEAM workspaces only; customers are reached via
              // the SaaS -> Customers list, not the switcher (spec matrix).
              `/organizations/${orgPublicId}/workspaces/search?q=${encodeURIComponent(q)}&limit=20&kind=team`,
            );
            const data = res.ok
              ? ((await res.json()) as { results: WorkspaceSearchResult[] })
              : { results: [] };
            setSearchResults(data.results);
          } catch {
            setSearchResults([]);
          } finally {
            setSearching(false);
          }
        })();
      },
      q ? 200 : 0,
    );
    return () => clearTimeout(handle);
  }, [query, orgPublicId, fetchWithAuth]);

  // Empty query → browse the first MAX_VISIBLE context workspaces (instant, no
  // fetch). Non-empty → the server hits, each labelled with the channel that
  // matched. Role for a hit is read back from the context (every accessible
  // workspace is already there); falls back to 'member' if somehow absent.
  const displayItems = useMemo<SwitcherItem[]>(() => {
    if (!query.trim()) {
      // Browse offers TEAM workspaces only (customers are reached via the SaaS
      // -> Customers list). Exact === 'team' so a null/unknown kind is excluded.
      return workspaces
        .filter((w) => w.kind === 'team')
        .slice(0, MAX_VISIBLE)
        .map((w) => ({
          publicId: w.id,
          name: w.name,
          subtitle: null,
          role: w.role,
        }));
    }
    return (searchResults ?? []).map((r) => ({
      publicId: r.publicId,
      name: r.name,
      subtitle: r.matchedChannel?.label ?? null,
      role: workspaces.find((w) => w.id === r.publicId)?.role ?? 'member',
    }));
  }, [query, workspaces, searchResults]);

  function handleSwitch(item: SwitcherItem) {
    try {
      // Phase 2 auth re-rooting: workspace switch is a pure in-app scope
      // change. X-Workspace-Id on subsequent requests updates automatically
      // via WorkspaceProvider's activeId; no POST /auth/switch-organization
      // is needed (org session covers all workspaces in the org).
      const id = item.publicId as Workspace['id'];
      switchWorkspace(id);
      emit(
        'workspace_switched',
        {
          from_workspace_id: activeWorkspace?.id ?? null,
          to_workspace_id: id,
        },
        { workspace_id: id },
      );
      setOpen(false);
      navigate(workspaceUrl(id));
    } catch {
      toast.error('Failed to switch workspace');
    }
  }

  async function handleCreate() {
    setCreating(true);
    try {
      const res = await fetchWithAuth('/workspaces', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: 'New Workspace' }),
      });
      if (res.ok) {
        // POST /workspaces returns the public DTO: `id` is the ws_ public id
        // (AIT-147 — was `publicId` on the old raw-row response).
        const ws = await res.json();
        // Phase 113 — funnel stage 5 marker. Capture BEFORE the
        // refreshWorkspaces()/switchWorkspace() side effects so the event
        // fires while the fresh response body is in scope.
        emit('workspace_created', {
          workspace_id: ws.id,
          // The public DTO no longer exposes createdAt (AIT-147); the event
          // fires at creation time, so stamping now keeps the registry shape.
          created_at: new Date().toISOString(),
        });
        await refreshWorkspaces();
        switchWorkspace(ws.id);
        toast.success('Workspace created');
        setOpen(false);
        navigate(workspaceUrl(ws.id));
      } else {
        toast.error('Failed to create workspace');
      }
    } catch {
      toast.error('Failed to create workspace');
    } finally {
      setCreating(false);
    }
  }

  if (!activeWorkspace) {
    return (
      <SidebarMenu>
        <SidebarMenuItem>
          {/* Placeholder matches the live trigger dimensions (h-12, px-4 gutter)
              so there's no shift when the switcher hydrates. Keeps the sidebar
              slot mounted so WorkspaceProvider's setActiveId flip doesn't
              trigger an unmount reflow = white flash. */}
          <div
            data-testid="workspace-switcher-skeleton"
            className="flex h-12 w-full items-center gap-2 px-4 group-data-[collapsible=icon]:px-2"
          >
            <Skeleton className="size-8 shrink-0 rounded-md" />
            <div className="grid flex-1 gap-1">
              <Skeleton className="h-4 w-24" />
              <Skeleton className="h-3 w-16" />
            </div>
            <Skeleton className="ml-auto size-4 shrink-0" />
          </div>
        </SidebarMenuItem>
      </SidebarMenu>
    );
  }

  return (
    <SidebarMenu>
      <SidebarMenuItem>
        <Combobox.Root
          items={displayItems}
          // Items are already filtered (browse list) or server-ranked (search
          // hits), so the built-in contains-matcher is disabled (filter={null}).
          filter={null}
          open={open}
          onOpenChange={(next) => {
            setOpen(next);
            if (!next) setQuery('');
          }}
          onInputValueChange={(value) => setQuery(value)}
          onValueChange={(item) => {
            if (item) handleSwitch(item as SwitcherItem);
          }}
          itemToStringLabel={(item: SwitcherItem) => item?.name ?? ''}
          isItemEqualToValue={(a: SwitcherItem, b: SwitcherItem) =>
            a?.publicId === b?.publicId
          }
        >
          <Combobox.Trigger
            render={
              <SidebarMenuButton
                size="lg"
                aria-label="Switch Workspace"
                className="h-12 rounded-none px-4 group-data-[collapsible=icon]:px-2 data-[popup-open]:bg-sidebar-accent data-[popup-open]:text-sidebar-accent-foreground"
              />
            }
          >
            <div className="flex size-8 items-center justify-center rounded-md bg-primary text-primary-foreground text-sm font-semibold shrink-0">
              {activeWorkspace.name.charAt(0).toUpperCase()}
            </div>
            <div className="grid flex-1 text-left text-sm leading-tight group-data-[collapsible=icon]:hidden">
              <span className="truncate font-semibold">
                {activeWorkspace.name}
              </span>
              <span className="truncate text-xs text-muted-foreground capitalize">
                {activeWorkspace.role}
              </span>
            </div>
            <ChevronsUpDown className="ml-auto size-4 group-data-[collapsible=icon]:hidden" />
          </Combobox.Trigger>

          <Combobox.Portal>
            <Combobox.Positioner positionMethod="fixed" className="z-50" align="start" side="bottom" sideOffset={4}>
              {/* Fixed width so the popup stays usable in BOTH the expanded
                  (16rem) sidebar and the collapsed icon rail (4rem) — anchor-width
                  math would shrink it to the rail width otherwise. */}
              <Combobox.Popup className="z-50 w-60 rounded-lg bg-popover p-1 text-popover-foreground shadow-md ring-1 ring-foreground/10 outline-none data-open:animate-in data-open:fade-in-0 data-open:zoom-in-95 data-closed:animate-out data-closed:fade-out-0 data-closed:zoom-out-95">
                <div className="flex items-center gap-2 rounded-md px-2 py-1.5">
                  {searching ? (
                    <Loader2 className="size-4 shrink-0 animate-spin text-muted-foreground" />
                  ) : (
                    <Search className="size-4 shrink-0 text-muted-foreground" />
                  )}
                  <Combobox.Input
                    placeholder="Workspace or channel"
                    className="w-full bg-transparent text-sm outline-none placeholder:text-muted-foreground"
                  />
                </div>
                <div className="-mx-1 my-1 h-px bg-foreground/10" />

                {/* base-ui keeps this aria-live region mounted for screen-reader
                    announcements even when there ARE matches (it just empties
                    the children). `empty:hidden` collapses the blank padding in
                    that case; when there's no match the text shows and it
                    expands. While a search is in flight we suppress the
                    "no match" copy to avoid a flash before results land. */}
                <Combobox.Empty className="px-2 py-6 text-center text-sm text-muted-foreground empty:hidden">
                  {searching
                    ? 'Searching…'
                    : query.trim()
                      ? 'No workspaces match your search'
                      : 'No workspaces found'}
                </Combobox.Empty>

                <Combobox.List className="max-h-52 overflow-y-auto no-visible-scrollbar">
                  {(item: SwitcherItem) => (
                    <Combobox.Item
                      key={item.publicId}
                      value={item}
                      className="flex cursor-pointer items-center gap-2 rounded-md p-2 text-sm outline-none select-none data-highlighted:bg-accent data-highlighted:text-accent-foreground"
                    >
                      <div className="flex size-6 items-center justify-center rounded-sm bg-primary text-primary-foreground text-xs font-semibold">
                        {item.name.charAt(0).toUpperCase()}
                      </div>
                      <span className="grid flex-1 leading-tight">
                        <span className="truncate">{item.name}</span>
                        {item.subtitle && (
                          <span className="truncate text-xs text-muted-foreground">
                            {item.subtitle}
                          </span>
                        )}
                      </span>
                      {item.publicId === activeWorkspace.id && (
                        <Check className="size-4 shrink-0" />
                      )}
                      <Badge
                        variant={item.role === 'admin' ? 'default' : 'secondary'}
                        className="text-xs capitalize"
                        aria-label={`Role: ${item.role}`}
                      >
                        {item.role}
                      </Badge>
                    </Combobox.Item>
                  )}
                </Combobox.List>

                {!query.trim() && workspaces.length > MAX_VISIBLE && (
                  <div className="px-2 py-1 text-center text-xs text-muted-foreground">
                    Showing {MAX_VISIBLE} of {workspaces.length}. Search for more.
                  </div>
                )}

                <div className="-mx-1 my-1 h-px bg-foreground/10" />
                <button
                  type="button"
                  onClick={handleCreate}
                  disabled={creating}
                  className="flex w-full cursor-pointer items-center gap-2 rounded-md p-2 text-sm outline-none hover:bg-accent hover:text-accent-foreground disabled:opacity-50"
                >
                  <Plus className="size-4" />
                  Create workspace
                </button>
              </Combobox.Popup>
            </Combobox.Positioner>
          </Combobox.Portal>
        </Combobox.Root>
      </SidebarMenuItem>
    </SidebarMenu>
  );
}
